import { generatePath } from 'react-router-dom'
import isFeatureEnabled, { FEATURES } from 'src/utils/isFeatureEnabled'
import { isAllowed, AUTH_VIEW_PERMISSIONS } from 'src/utils/permissions'

class RouteError extends Error {
    constructor(msg) {
        super('RouteError: ' + msg)
    }
}

export const routeConfig = [
    {
        path: '/logout',
        title: 'Logout',
        component: './Logout',
        name: 'logout',
    },
    {
        path: '/notifications/:subPath?',
        title: 'Notifications',
        component: './mfes/NotificationsMfe',
        name: 'notifications',
    },
    // MFE-EXAMPLE - only to be enabled on tst
    {
        path: '/mfe/example/:subPath?',
        component: './mfes/ExampleMfe',
        name: 'example-mfe',
        crossMfeKey: 'EXAMPLE_MFE',
        permissions: () => {
            return isFeatureEnabled(FEATURES.EXAMPLE_MFE_MENU)
        },
    },
    {
        path: '/mfe/auth',
        component: './mfes/AuthMfe',
        name: 'auth-mfe',
        crossMfeKey: 'AUTH_MFE',
        permissions: context => isAllowed(context, AUTH_VIEW_PERMISSIONS),
    },
    {
        path: '/unauthorized',
        title: 'Unauthorized',
        component: './UnauthorizedPage',
        name: 'unauthorized',
    },
    {
        path: '/server-error',
        title: 'Server error',
        component: './ErrorPages/ServerErrorPage',
        name: 'server-error',
    },
    {
        path: '/not-found',
        title: 'Page not found: 404',
        component: './ErrorPages/NotFound',
        name: 'not-found',
    },
    {
        path: '/',
        title: 'Home',
        component: './Home',
        name: 'home',
        exact: true,
    },
]

/**
 * @param name
 * @returns {{path: string, component: string, permissions: (function(*): *), name: string, title: string}|{path: string, component: string, permissions: (function(*): *), name: string, title: string}|{path: string, component: string, permissions: (function(*): *), name: string, title: string}|{path: string, component: string, name: string, title: string}|{path: string, component: string, name: string, title: string}}
 */
function findRouteByName(name) {
    const routes = routeConfig.filter(route => route.name === name)
    if (routes.length === 0) {
        throw new RouteError(`No matches found in routeConfig for ${name}`)
    } else if (routes.length > 1) {
        throw new RouteError(
            `Multiple matches found in routeConfig for ${name}: ${routes
                .map(r => r.path)
                .join(', ')}`
        )
    }
    return routes[0]
}

/**
 * Looks up the path of a route using the routeName.
 * @param {string} name - name of the route.
 * @param {Object} params - dynamic params to include in the path
 * @param {Object} queryParams - query parameters
 * @param {string} [hash=''] - hash fragment to append to the URL.
 * @returns {string} - generated URL
 */
export const reverse = (name, params = {}, queryParams = {}, hash = '') => {
    const route = findRouteByName(name)
    const path = generatePath(route.path, params)
    let url = path

    if (Object.entries(queryParams).length) {
        const searchParams = new URLSearchParams(queryParams)
        url += `?${searchParams.toString()}`
    }

    if (hash) {
        url += `#${hash}`
    }

    return url
}

/**
 * Function used for checking permissions using the route name.
 * This unburdens the user from worrying about what permissions to check for a certain route.
 * @param userData
 * @param name
 * @returns {boolean}
 */
export const isRouteAllowed = (userData, name) => {
    const route = findRouteByName(name)
    return 'permissions' in route
        ? route.permissions(userData, route.name)
        : true
}
